import { TINT } from "./colour";
import { Terrain } from "../replay/format";
import type { Statics } from "../replay/replay";
import { GridTexture, Program, rgb, type GL } from "./gl";

/** How the zoomed-out view colours hexes. */
export enum OverviewMode {
  Armies = 0,
  Ammo = 1,
  Suppression = 2,
  Terrain = 3,
}

export const Layer = {
  TRACK: 1,
  CORRIDORS: 2,
  ZONES: 4,
  GRID: 8,
} as const;

export interface GroundView {
  center: [number, number];
  zoom: number;
  viewport: [number, number];
  /** 0 = overview, 1 = detail (units drawn as sprites). */
  detail: number;
  mode: OverviewMode;
  /** Saturation channel: 0 none, 1 health, 2 ammo, 3 suppression. */
  sat: number;
  layers: number;
  /** Bit per faction whose corridors show. */
  corridorMask: number;
  hover: [number, number];
  /** Crossfade from the previous frame (0) to the current (1). */
  blend: number;
  /** Fog of war (`setVisibility`): 0 off, else 1 + the side whose sight shows. */
  fog: number;
  /** Close-up terrain detail and soft borders (Settings > Graphics). */
  fancy?: boolean;
  /** The colour off the map (default: the page's background). */
  void?: [number, number, number];
  /** Draw settled ground (default: when known). */
  territory?: boolean;
  /** Draw the ore heatmap (default: when known). */
  ore?: boolean;
  /** The storm wall at the world's edge, and the shards beyond it (off by
   *  default: the minimap keeps a clean frame). */
  storm?: boolean;
  /** Seconds, for the storm's motion. */
  time?: number;
}

const VERTEX = `#version 300 es
void main() {
  vec2 p = vec2((gl_VertexID << 1) & 2, gl_VertexID & 2);
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

const FRAGMENT = `#version 300 es
precision highp float;
precision highp int;
uniform vec2 uCenter;
uniform float uZoom;
uniform vec4 uViewport; // x, y, w, h in device pixels
uniform ivec2 uWorld;
uniform int uZone;
uniform sampler2D uTerrain;  // colour, mipmapped
uniform sampler2D uOverlay;  // r: track, g: corridor bits
uniform sampler2D uOcc;      // armies: rgb their colour times fill (premultiplied), a fill; mipmapped
uniform sampler2D uStatus;   // r: mean ammo, g: suppressed share, b: mean health, a: recent deaths
uniform sampler2D uOccPrev;  // the previous frame's, to crossfade from
uniform sampler2D uStatusPrev;
uniform float uBlend;        // 0 = previous frame, 1 = current
uniform sampler2D uOwn;      // under fog, the viewer's own fill (r), kept apart from uOcc
uniform sampler2D uOwnPrev;
uniform vec3 uOwnColor;
uniform float uDetail;
uniform int uMode;
uniform int uSat;
uniform sampler2D uTint;     // rgb: tint hue x tinted share, a: tinted share
uniform sampler2D uTintPrev;
uniform float uTintAmount;
uniform int uLayers;
uniform int uCorridorMask;
uniform ivec2 uHover;
uniform sampler2D uWater;    // r: water share, mipmapped
uniform sampler2D uVis;      // r: 1 in sight, ~0.45 seen before, 0 never seen
uniform int uFog;
uniform vec3 uVoid;
uniform int uFancy;            // 0 off, else 1 + the side whose sight shows
uniform sampler2D uTerritory; // settled ground per cell: rgb faction colour x settlement, a settlement
uniform vec2 uTerritoryScale; // world uv to territory uv (cells cover a little more than the map)
uniform int uHasTerritory;
uniform sampler2D uOre;       // what prospecting found per cell: rgb heat colour x a, a its strength
uniform vec2 uOreScale;       // world uv to ore uv (as uTerritoryScale)
uniform int uHasOre;
uniform int uStorm;
uniform float uTime;
// Regions (when the world has them): per hex r wall, g outside, b void,
// a inside (filtered, so edges are soft); and the layer before a breach.
uniform sampler2D uRegion;
uniform sampler2D uRegionPrev;
uniform int uHasRegion;
uniform vec2 uBreach;        // the breaker's hex (axial)
uniform float uBreachT;      // seconds since the breach (< 0: none)
out vec4 outColor;

const float SQRT3 = 1.7320508;

ivec2 hexRound(vec2 f) {
  float s = -f.x - f.y;
  vec3 c = vec3(f.x, f.y, s);
  vec3 r = round(c);
  vec3 d = abs(r - c);
  if (d.x > d.y && d.x > d.z) r.x = -r.y - r.z;
  else if (d.y > d.z) r.y = -r.x - r.z;
  return ivec2(r.xy);
}

vec2 center(ivec2 h) {
  return vec2(SQRT3 * (float(h.x) + float(h.y) * 0.5), 1.5 * float(h.y));
}

bool inWorld(ivec2 h) {
  return h.x >= 0 && h.y >= 0 && h.x < uWorld.x && h.y < uWorld.y;
}

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float vnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y);
}

float fbm(vec2 p) {
  float s = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) {
    s += a * vnoise(p);
    p = p * 2.03 + vec2(17.1, 9.2);
    a *= 0.5;
  }
  return s;
}

// The storm wall: blue weather rolling along the world's edge (world
// position in hex units; 0..1, brighter where it's thick).
const vec3 STORM = vec3(0.2, 0.5, 1.0);
float storm(vec2 w) {
  float t = uTime;
  return fbm(w * 0.045 + vec2(t * 0.3, -t * 0.2)) * 0.6 + fbm(w * 0.16 - vec2(t * 0.8, t * 0.35)) * 0.4;
}

// A region sample at axial position fa (continuous): outside the canvas,
// void.
vec4 regionAt(sampler2D tex, vec2 fa, float lod) {
  vec2 ruv = (fa + 0.5) / vec2(uWorld);
  if (ruv.x < 0.0 || ruv.y < 0.0 || ruv.x > 1.0 || ruv.y > 1.0) return vec4(0.0, 0.0, 1.0, 0.0);
  return textureLod(tex, ruv, lod);
}

// The ground as its region shows it: inside as it is; outside veiled in
// the storm's haze, greyed but readable, fading over several hexes into
// void, the ground further veiled; the wall the storm itself, dark weather
// with bright crests, spilling a little either side. rg: the regions here; soft: the same blurred (a few hexes).
vec3 regionColor(vec3 c, vec4 rg, vec4 soft, float n) {
  float lum = dot(c, vec3(0.299, 0.587, 0.114));
  vec3 veiled = mix(vec3(lum), c, 0.5) * 0.9;
  veiled = mix(veiled, vec3(0.12, 0.19, 0.3), 0.14 + 0.16 * n);
  float crest = smoothstep(0.5, 0.78, n);
  vec3 wall = mix(STORM * (0.3 + 0.45 * n), STORM * 1.05, crest);
  wall = mix(wall, vec3(0.75, 0.88, 1.0), smoothstep(0.72, 0.9, n) * 0.6);
  // Beyond the haze (void): still the world's ground, out to the map's
  // edge, but further veiled (greyer, darker, under the weather), so the
  // view doesn't end a few hexes past the wall.
  vec3 voidc = mix(vec3(lum), c, 0.3) * 0.62;
  voidc = mix(voidc, vec3(0.06, 0.09, 0.15) + STORM * 0.08 * n, 0.3 + 0.2 * n);
  // Outside fades out toward void (by the blurred void share, broken up
  // by the weather).
  float gone = clamp(soft.b * 1.6 - 0.3 + (n - 0.5) * 0.35, 0.0, 1.0);
  float beyond = rg.g + rg.b;
  float sum = rg.r + beyond + rg.a + 1e-4;
  vec3 col = (c * rg.a + mix(veiled, voidc, gone) * beyond + wall * rg.r) / sum;
  // The storm spills over either side: cloud where it's thick.
  float spill = smoothstep(0.04, 0.3, soft.r) * smoothstep(0.3, 0.75, n) * (1.0 - rg.r / sum);
  col = mix(col, wall, spill * 0.75);
  col += STORM * 0.1 * n * soft.r * rg.a / sum;
  return col;
}

float segment(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a, ba = b - a;
  float t = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * t);
}

// occ: everyone's colours premultiplied by fill (any number of factions,
// blended per hex before upload); own: the viewer's own fill under fog.
vec3 armies(vec4 occ, float own, vec4 st, vec4 tint) {
  float total = occ.a + own;
  if (total <= 0.0) return vec3(0.0);
  vec3 c = (occ.rgb + uOwnColor * own) / total;
  // Tint toward the mean hue of tinted units, by their share.
  if (tint.a > 0.002) c = mix(c, tint.rgb / tint.a, uTintAmount * clamp(tint.a * 1.5, 0.0, 1.0));
  // Saturation shows an analog measure: 1 mean health (b), 2 mean ammo (r),
  // 3 suppression (1 - suppressed share, g).
  float s = uSat == 1 ? st.b : uSat == 2 ? st.r : uSat == 3 ? 1.0 - st.g : 1.0;
  float lum = dot(c, vec3(0.299, 0.587, 0.114));
  float v = clamp(s, 0.0, 1.0);
  return mix(vec3(lum), c, 0.12 + 0.88 * v * v);
}

void main() {
  vec2 fc = gl_FragCoord.xy - uViewport.xy;
  vec2 p = vec2(fc.x - uViewport.z * 0.5, uViewport.w * 0.5 - fc.y);
  vec2 w = uCenter + p / uZoom;
  vec2 f = vec2(SQRT3 / 3.0 * w.x - w.y / 3.0, w.y * 2.0 / 3.0);
  ivec2 h = hexRound(f);
  vec2 world = vec2(uWorld);
  // Continuous texture coordinates for filtered (zoomed-out) sampling.
  vec2 uv = (f + 0.5) / world;
  bool inside = inWorld(h);
  // Hexes past the world's edge (negative inside).
  float past = max(max(-uv.x, uv.x - 1.0) * world.x, max(-uv.y, uv.y - 1.0) * world.y);
  if (!inside && uHasRegion != 0) {
    // Past the canvas: void (the region layer draws the wall within it).
    outColor = vec4(uVoid, 1.0);
    return;
  }
  if (!inside && (uv.x < -0.002 || uv.y < -0.002 || uv.x > 1.002 || uv.y > 1.002)) {
    if (uStorm == 0) {
      outColor = vec4(uVoid, 1.0);
      return;
    }
    // Beyond the wall, on every side and corner alike: neighbouring ground
    // seen through the storm (heavily, not completely), fading into
    // nothing at the same distance all round.
    float n = storm(w);
    vec3 cc = vec3(0.04, 0.07, 0.13);
    // Distance out from the map's edge as seen (in hexes), not along the
    // sheared axes, so the slanted edges and the corners match the others.
    vec2 fe = clamp(f, vec2(-0.5), world - 0.5);
    float beyond = length(w - vec2(SQRT3 * (fe.x + fe.y * 0.5), 1.5 * fe.y)) / SQRT3;
    // Another world's ground: its own lie of the land (the map turned
    // about), too blurred and veiled to read closely.
    vec2 mu = 1.0 - abs(mod(uv + 1.0, 2.0) - 1.0);
    vec3 far = textureLod(uTerrain, mu, 4.0).rgb;
    float lum = dot(far, vec3(0.299, 0.587, 0.114));
    far = mix(vec3(lum), far, 0.5) * 0.85;
    cc = mix(far, mix(cc, STORM * 0.45, n * n), 0.38 + 0.3 * n);
    // The wall itself, thickest at the edge.
    cc = mix(cc, STORM * (0.5 + 0.9 * n), exp(-beyond / 12.0) * 0.85);
    float reach = min(world.x, world.y);
    cc = mix(cc, uVoid, smoothstep(reach * 0.55, reach, beyond));
    outColor = vec4(cc, 1.0);
    return;
  }
  vec2 tc = (vec2(h) + 0.5) / world;

  // Terrain: exact per hex when zoomed in, filtered when zoomed out.
  vec3 terrain = uDetail > 0.0 ? texture(uTerrain, tc).rgb : texture(uTerrain, uv).rgb;
  vec3 c = terrain;
  if (inside && uDetail > 0.0 && uFancy != 0) {
    const ivec2 nb[6] = ivec2[6](ivec2(1,0), ivec2(1,-1), ivec2(0,-1), ivec2(-1,0), ivec2(-1,1), ivec2(0,1));
    vec2 local = w - center(h);
    int type = int(texelFetch(uOverlay, h, 0).b * 255.0 + 0.5);
    // The neighbour across the nearest edge, and how near that edge is.
    int k = 0;
    float best = -1e9;
    for (int i = 0; i < 6; i++) {
      float dd = dot(local, center(h + nb[i]) - center(h));
      if (dd > best) { best = dd; k = i; }
    }
    ivec2 n = h + nb[k];
    float e = max(abs(local.x), max(abs(0.5 * local.x + 0.866 * local.y), abs(0.5 * local.x - 0.866 * local.y))) / 0.8660254;
    if (inWorld(n)) {
      // Soft borders between ground types: half and half at the edge.
      vec3 other = texelFetch(uTerrain, n, 0).rgb;
      c = mix(c, other, 0.5 * smoothstep(0.45, 1.0, e));
      int ntype = int(texelFetch(uOverlay, n, 0).b * 255.0 + 0.5);
      // Shorelines: a pale band where water meets land.
      bool wet = type == 2 || type == 5;
      bool nwet = ntype == 2 || ntype == 5 || ntype == 6;
      if (wet && !nwet) c = mix(c, vec3(0.42, 0.55, 0.6), 0.45 * smoothstep(0.7, 0.98, e));
    }
    // Close-up detail, fading in with zoom: canopy, rock, grass.
    float near = clamp((uZoom - 5.0) / 10.0, 0.0, 1.0);
    if (near > 0.0) {
      float px = 1.5 / uZoom;
      if (type == 4) {
        float tree = 0.0;
        float lit = 0.0;
        for (int i = 0; i < 4; i++) {
          vec2 o = vec2(hash(vec2(h) + float(i) * 7.1), hash(vec2(h) + float(i) * 3.7 + 11.0)) * 1.1 - 0.55;
          float r = 0.26 + 0.1 * hash(vec2(h) + float(i) * 1.9);
          float dist = length(local - o) - r;
          tree = max(tree, 1.0 - smoothstep(-px, px, dist));
          lit = max(lit, (1.0 - smoothstep(-px, px, length(local - o + vec2(0.07, 0.08)) - r * 0.55)));
        }
        c = mix(c, c * 0.62, tree * near);
        c = mix(c, c * 1.25 + vec3(0.01, 0.025, 0.005), lit * 0.5 * near);
      } else if (type == 3) {
        float g = hash(floor(local * 6.0) + vec2(h) * 13.0);
        float ridge = abs(fract((local.x * 0.8 + local.y * 0.6) * 2.2 + hash(vec2(h)) ) - 0.5);
        c *= 1.0 + near * (0.12 * (g - 0.5) - 0.18 * smoothstep(0.1, 0.0, ridge));
      } else if (type == 0 || type == 1) {
        float g = hash(floor(local * 9.0) + vec2(h) * 17.0);
        c *= 1.0 + near * 0.05 * (g - 0.5);
      }
    }
  }
  // Zoomed out, a hex-wide river would average into the land: anywhere with
  // a little water reads as water, a map's rivers drawn wider than life.
  if (uDetail < 1.0) {
    float wet = texture(uWater, uv).r;
    vec3 river = vec3(0.13, 0.27, 0.40);
    c = mix(c, river, smoothstep(0.08, 0.4, wet) * (1.0 - uDetail) * 0.85);
  }
  // Settled ground: a wash of its settler's colour, stronger where it's
  // more settled (the texture filters, so borders are soft).
  if (uHasTerritory != 0) {
    vec4 t = texture(uTerritory, uv * uTerritoryScale);
    if (t.a > 0.004) c = mix(c, t.rgb / t.a, t.a * 0.3);
  }
  // Fog of war: what's out of sight darkens; never seen, nearly black.
  if (uFog != 0) {
    float v = texture(uVis, uv).r;
    float lum = dot(c, vec3(0.299, 0.587, 0.114));
    // The map is known; what's on it isn't: out of sight is dimmed and
    // greyed, never-seen ground more so.
    // Light enough that the land (where to dig, where to go) still reads.
    vec3 remembered = mix(vec3(lum), c, 0.5) * 0.74;
    vec3 unknown = mix(vec3(lum), c, 0.3) * 0.56;
    c = v > 0.45 ? mix(remembered, c, smoothstep(0.45, 0.9, v)) : mix(unknown, remembered, smoothstep(0.0, 0.45, v));
  }

  // Ore found: a heatmap over prospected ground (knowledge, not sight, so
  // after the fog), blending softly from cell to cell.
  if (uHasOre != 0) {
    vec4 o = texture(uOre, uv * uOreScale);
    if (o.a > 0.004) c = mix(c, o.rgb / o.a, o.a);
  }

  // The storm wall's inner face: blue weather a few hexes in, a faint
  // glow farther.
  if (uHasRegion == 0 && uStorm != 0 && past > -40.0) {
    float n = storm(w);
    c = mix(c, STORM * (0.5 + 0.9 * n), clamp(exp(past / 2.5), 0.0, 1.0) * 0.75);
    c += STORM * 0.08 * n * exp(past / 10.0);
  }

  // Regions: the storm wall where the world says, the ground beyond it
  // veiled, void past that. Edges wander a hex or so, drifting slowly, so
  // the wall reads as weather, not a fence.
  if (uHasRegion != 0) {
    float n = storm(w);
    vec2 jit = vec2(vnoise(w * 0.11 + vec2(uTime * 0.05, 0.0)), vnoise(w * 0.11 + vec2(31.0, -uTime * 0.05))) - 0.5;
    vec2 fj = f + jit * 2.4;
    vec3 now = regionColor(c, regionAt(uRegion, fj, 0.0), regionAt(uRegion, fj, 3.0), n);
    // The map's own edge: the veiled ground fades out over its last hexes
    // instead of stopping at a line.
    float edge = min(min(f.x, f.y), min(world.x - 1.0 - f.x, world.y - 1.0 - f.y));
    now = mix(uVoid, now, smoothstep(0.0, 28.0, edge));
    if (uBreachT >= 0.0) {
      // A breach: a front runs out from the breaker over three seconds,
      // easing off; each hex turns from the old regions to the new as it
      // passes, a beat apart (the ground clearing, the void's edge coming
      // up as ground), a flat ring riding the front, and the broken wall
      // flung outward ahead of it in pieces.
      const float DUR = 3.2;
      const float REACH = 84.0;
      float tau = clamp(uBreachT / DUR, 0.0, 1.0);
      float front = REACH * (1.0 - pow(1.0 - tau, 3.0));
      vec2 bw = vec2(SQRT3 * (uBreach.x + uBreach.y * 0.5), 1.5 * uBreach.y);
      float d = length(w - bw) / SQRT3;
      vec3 before = regionColor(c, regionAt(uRegionPrev, fj, 0.0), regionAt(uRegionPrev, fj, 3.0), n);
      float turn = clamp((front - d - 4.0 * hash(vec2(h) * 0.37)) / 5.0, 0.0, 1.0);
      now = mix(before, now, turn);
      // The wall that stood here, flung out with the front in pieces: a
      // wall hex d0 out lands at d0 + 0.6 (front - d0), scattered a little
      // sideways, shrinking to nothing. Three candidates along the ray (the
      // pieces overlap their neighbours).
      if (d < front + 2.0 && tau < 1.0) {
        float a = 0.6;
        vec2 dir = (w - bw) / max(1e-3, length(w - bw));
        float d0 = (d - a * front) / (1.0 - a);
        float life = 1.0 - tau;
        float pieces = 0.0;
        for (int k = -1; k <= 1; k++) {
          float dk = d0 + float(k) * 0.8;
          if (dk <= 0.0 || dk > front) continue;
          vec2 sw = bw + dir * dk * SQRT3;
          ivec2 sh = hexRound(vec2(SQRT3 / 3.0 * sw.x - sw.y / 3.0, sw.y * 2.0 / 3.0));
          if (regionAt(uRegionPrev, vec2(sh), 0.0).r < 0.5 || regionAt(uRegion, vec2(sh), 0.0).r > 0.5) continue;
          vec2 sc = center(sh);
          vec2 away = (sc - bw) / max(1e-3, length(sc - bw));
          float dc = length(sc - bw) / SQRT3;
          float at = dc + a * max(0.0, front - dc);
          vec2 side = vec2(-away.y, away.x) * (hash(vec2(sh) * 2.3) - 0.5) * (at - dc) * 0.5 * SQRT3;
          vec2 pc = bw + away * at * SQRT3 + side;
          float size = (0.55 + 0.45 * hash(vec2(sh) * 1.7)) * life;
          pieces = max(pieces, 1.0 - smoothstep(size - 0.12, size, length(w - pc) / SQRT3));
        }
        now = mix(now, STORM * (0.6 + 0.6 * n), pieces * smoothstep(0.0, 0.2, life));
      }
      // The shockwave: a thin flat ring on the front, fading as it goes.
      float ring = 1.0 - smoothstep(0.4, 1.4, abs(d - front));
      now = mix(now, vec3(0.7, 0.86, 1.0), ring * 0.65 * (1.0 - tau));
    }
    c = now;
  }

  // Armies, filtered when zoomed out, fading out as units become sprites.
  if (uMode != 3) {
    vec2 at = uDetail > 0.5 ? tc : uv;
    vec4 occ = mix(texture(uOccPrev, at), texture(uOcc, at), uBlend);
    float own = 0.0;
    // Fog: other sides show only in sight (premultiplied, so scaling the
    // whole texel fades them out cleanly).
    if (uFog != 0) {
      own = mix(texture(uOwnPrev, at).r, texture(uOwn, at).r, uBlend);
      occ *= smoothstep(0.8, 0.95, texture(uVis, at).r);
    }
    vec4 st = mix(texture(uStatusPrev, at), texture(uStatus, at), uBlend);
    float fill = occ.a + own;
    vec4 tint = mix(texture(uTintPrev, at), texture(uTint, at), uBlend);
    vec3 army = armies(occ, own, st, tint);
    float a = clamp(fill * 2.2, 0.0, 1.0) * (1.0 - 0.8 * uDetail);
    c = mix(c, army, a);
    // Recent deaths glow in the overview.
    c += vec3(0.9, 0.2, 0.1) * st.a * 0.6 * (1.0 - uDetail);
  }

  if (inside && uDetail > 0.0) {
    vec2 local = w - center(h);
    vec4 ov = texelFetch(uOverlay, h, 0);
    int corridor = int(ov.g * 255.0 + 0.5);
    if ((uLayers & 2) != 0 && (corridor & uCorridorMask) != 0) {
      c = mix(c, vec3(0.75, 0.68, 0.45), 0.16 * uDetail);
    }
    // Track: a line from the centre toward each neighbouring track hex.
    if ((uLayers & 1) != 0 && ov.r > 0.5) {
      const ivec2 dirs[6] = ivec2[6](ivec2(1,0), ivec2(1,-1), ivec2(0,-1), ivec2(-1,0), ivec2(-1,1), ivec2(0,1));
      float d = 1e9;
      bool any = false;
      for (int k = 0; k < 6; k++) {
        ivec2 n = h + dirs[k];
        if (inWorld(n) && texelFetch(uOverlay, n, 0).r > 0.5) {
          vec2 to = center(n) - center(h);
          d = min(d, segment(local, vec2(0.0), to * 0.5));
          any = true;
        }
      }
      if (!any) d = length(local);
      float px = 1.0 / uZoom;
      float rail = 1.0 - smoothstep(0.07, 0.07 + 1.5 * px, d);
      float sleepers = 1.0 - smoothstep(0.16, 0.16 + 1.5 * px, d);
      c = mix(c, vec3(0.30, 0.24, 0.18), sleepers * 0.6 * uDetail);
      c = mix(c, vec3(0.62, 0.50, 0.38), rail * uDetail);
    }
    // Hex edges, anti-aliased, fading in with zoom.
    if ((uLayers & 8) != 0) {
      float e = max(abs(local.x), max(abs(0.5 * local.x + 0.866 * local.y), abs(0.5 * local.x - 0.866 * local.y))) / 0.8660254;
      float px = 1.0 / (uZoom * 0.8660254);
      float edge = smoothstep(1.0 - 1.5 * px, 1.0, e);
      float show = clamp((uZoom - 6.0) / 10.0, 0.0, 1.0) * uDetail;
      c = mix(c, c * 0.55, edge * show);
    }
    if (h == uHover) c = mix(c, vec3(1.0), 0.12);
  }

  // Zone borders (the unit of sharding).
  if ((uLayers & 4) != 0 && inside && uZone > 0) {
    ivec2 m = h % uZone;
    float px = 1.0 / uZoom;
    bool border = m.x == 0 || m.y == 0;
    if (border) c = mix(c, vec3(0.55, 0.6, 0.75), 0.35);
  }

  outColor = vec4(c, 1.0);
}`;

/** What high ground drifts toward: dry, pale grassland. */
const UPLAND = rgb("#5f604c");

// Open, hills, water, mountains, forest, ford, bridge: natural but subdued,
// so the armies' saturated colours read on top.
const TERRAIN_COLORS = ["#34432f", "#4c4632", "#1b3b57", "#57534e", "#1f3423", "#33607a", "#1b3b57"].map(rgb);

/** Light from the relief layer: on land a hillshade lit from the upper left
 *  (slopes over two hexes, so plains show their roll and not single-hex
 *  noise), and high ground a little brighter. 1 where there's no relief. */
function landLight(relief: Uint8Array | null, terrain: Uint8Array, width: number, height: number): Float32Array {
  const light = new Float32Array(width * height).fill(1);
  if (!relief) return light;
  const e = (q: number, r: number) =>
    relief[Math.min(height - 1, Math.max(0, r)) * width + Math.min(width - 1, Math.max(0, q))] / 255;
  for (let r = 0; r < height; r++) {
    for (let q = 0; q < width; q++) {
      const i = r * width + q;
      const t = terrain[i];
      if (t === Terrain.FORD || t === Terrain.BRIDGE) continue;
      // Water's relief is its shallowness at sea (0 deep to 40 at the shore;
      // lakes sit higher): deep water darker, shallows lighter.
      if (t === Terrain.WATER) {
        light[i] = 0.7 + 0.3 * Math.min(1, relief[i] / 40);
        continue;
      }
      // Screen x runs along q; screen y along r, shifted half a hex per row.
      const slope = (k: number) => {
        const dx = (e(q + k, r) - e(q - k, r)) / (2 * k);
        const dy = (e(q, r + k) - e(q, r - k)) / (2 * k) - dx * 0.5;
        return dx + dy;
      };
      const fine = Math.tanh(-slope(2) * 28) * 0.22;
      const broad = Math.tanh(-slope(8) * 60) * 0.3;
      light[i] = (1 + fine + broad) * (0.82 + 0.4 * relief[i] / 255);
    }
  }
  return light;
}

/** The ground under the railway: track hexes (bridges over water, cuttings
 *  through rock) take the most common type among their non-track
 *  neighbours, so lines don't show as stripes of their own; the track is
 *  drawn on top. */
function groundUnderTrack(terrain: Uint8Array, track: Uint8Array, width: number, height: number): Uint8Array {
  const out = terrain.slice();
  const dirs = [
    [1, 0],
    [1, -1],
    [0, -1],
    [-1, 0],
    [-1, 1],
    [0, 1],
  ];
  const counts = new Uint8Array(8);
  for (let r = 0; r < height; r++) {
    for (let q = 0; q < width; q++) {
      const i = r * width + q;
      if (!track[i]) continue;
      counts.fill(0);
      for (const [dq, dr] of dirs) {
        const nq = q + dq;
        const nr = r + dr;
        if (nq < 0 || nr < 0 || nq >= width || nr >= height) continue;
        const j = nr * width + nq;
        if (!track[j]) counts[terrain[j]]++;
      }
      let best = -1;
      for (let t = 0; t < 8; t++) if (counts[t] > (best < 0 ? 0 : counts[best])) best = t;
      // Bridges show as water even when every neighbour is track too.
      if (best >= 0) out[i] = best;
      else if (terrain[i] === Terrain.BRIDGE) out[i] = Terrain.WATER;
    }
  }
  return out;
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** The ground last built, kept across the world being sent again (same
 *  canvas, size and lie of the land): building it anew costs most of a
 *  second on a 2048-hex map. */
let kept: { gl: GL; width: number; height: number; terrain: Uint8Array; relief: Uint8Array | null; ground: Ground; statics: Statics } | null = null;

/** The ground for a world: the one kept, refreshed, if it's the same land;
 *  else a new one (the old one's textures freed). */
export function groundFor(gl: GL, width: number, height: number, statics: Statics, zone: number): Ground {
  const k = kept;
  // The very same map (a reconnect that got identical bytes): as it is.
  if (k && k.gl === gl && k.statics === statics && k.width === width && k.height === height) return k.ground;
  const same =
    !!k &&
    k.gl === gl &&
    k.width === width &&
    k.height === height &&
    sameBytes(k.terrain, statics.terrain) &&
    (k.relief === null) === (statics.relief === null) &&
    (!statics.relief || sameBytes(k.relief!, statics.relief));
  if (same) {
    k!.ground.refresh(statics);
    k!.statics = statics;
    return k!.ground;
  }
  k?.ground.dispose();
  const ground = new Ground(gl, width, height, statics, zone);
  kept = { gl, width, height, terrain: statics.terrain.slice(), relief: statics.relief?.slice() ?? null, ground, statics };
  return ground;
}

/** Regions as one channel each (r wall, g outside, b void, a inside), so
 *  filtering blends them. */
function regionRGBA(region: Uint8Array): Uint8Array {
  const out = new Uint8Array(region.length * 4);
  const channel = [3, 0, 1, 2];
  for (let i = 0; i < region.length; i++) out[i * 4 + (channel[region[i]] ?? 2)] = 255;
  return out;
}

export class Ground {
  private program: Program;
  private terrain: GridTexture;
  readonly overlay: GridTexture;
  occ: GridTexture;
  status: GridTexture;
  /** The previous frame's, crossfaded from as units move. */
  occPrev: GridTexture;
  /** Settled ground at cell resolution (`setTerritory`), if known. */
  private territory: GridTexture | null = null;
  private territoryScale: [number, number] = [1, 1];
  /** The ore heatmap at cell resolution (`setOre`), if any. */
  private ore: GridTexture | null = null;
  private oreScale: [number, number] = [1, 1];
  /** Under fog, the viewer's own fill (red), apart from everyone else's. */
  own: GridTexture;
  ownPrev: GridTexture;
  statusPrev: GridTexture;
  tint: GridTexture;
  tintPrev: GridTexture;
  private overlayData: Uint8Array;
  private vao: WebGLVertexArrayObject;
  /** Fog of war: per hex, 255 in sight, 115 seen before, 0 never. */
  private vis: GridTexture;
  /** Water per hex, mipmapped: zoomed out, rivers stay drawn. */
  private water: GridTexture;
  /** Regions (`Statics.region`), one channel each, mipmapped; and the
   *  layer before the last breach, for its animation. */
  private region: GridTexture | null = null;
  private regionPrev: GridTexture | null = null;
  private breachAt: [number, number] = [0, 0];
  /** Seconds since the breach, while it plays (else -1). */
  breachT = -1;

  constructor(
    private gl: GL,
    private width: number,
    private height: number,
    statics: Statics,
    private zoneSize: number,
  ) {
    this.program = new Program(gl, VERTEX, FRAGMENT);
    this.vao = gl.createVertexArray()!;
    const tiles = width * height;
    const terrain = new Uint8Array(tiles * 4);
    const types = groundUnderTrack(statics.terrain, statics.track, width, height);
    const light = landLight(statics.relief, types, width, height);
    const water = new Uint8Array(tiles);
    for (let i = 0; i < tiles; i++) {
      let [r, g, b] = TERRAIN_COLORS[types[i]] ?? TERRAIN_COLORS[0];
      const t = types[i];
      const land = t === Terrain.OPEN || t === Terrain.ROUGH || t === Terrain.FOREST;
      if (land && statics.relief) {
        const up = Math.min(1, Math.max(0, (statics.relief[i] / 255 - 0.35) / 0.5));
        const [ur, ug, ub] = UPLAND;
        const k = up * up * 0.45;
        r += (ur - r) * k;
        g += (ug - g) * k;
        b += (ub - b) * k;
      }
      if (t === Terrain.WATER || t === Terrain.FORD) water[i] = 255;
      // A little per-hex variation so open ground isn't flat.
      const n = (((i * 2654435761) >>> 0) % 1000) / 1000 - 0.5;
      const l = light[i];
      terrain[i * 4] = Math.round(Math.min(1, (r + n * 0.012) * l) * 255);
      terrain[i * 4 + 1] = Math.round(Math.min(1, (g + n * 0.012) * l) * 255);
      terrain[i * 4 + 2] = Math.round(Math.min(1, (b + n * 0.012) * l) * 255);
      terrain[i * 4 + 3] = 255;
    }
    const rgba = { internal: gl.RGBA8, format: gl.RGBA, type: gl.UNSIGNED_BYTE };
    this.terrain = new GridTexture(gl, width, height, { ...rgba, mipmaps: true }, terrain);
    this.overlayData = new Uint8Array(tiles * 4);
    for (let i = 0; i < tiles; i++) {
      this.overlayData[i * 4] = statics.track[i] ? 255 : 0;
      this.overlayData[i * 4 + 1] = statics.corridor[i];
      this.overlayData[i * 4 + 2] = types[i];
    }
    this.vis = new GridTexture(gl, width, height, { internal: gl.R8, format: gl.RED, type: gl.UNSIGNED_BYTE, mipmaps: true }, null);
    this.water = new GridTexture(gl, width, height, { internal: gl.R8, format: gl.RED, type: gl.UNSIGNED_BYTE, mipmaps: true }, water);
    this.overlay = new GridTexture(gl, width, height, rgba, this.overlayData);
    this.occ = new GridTexture(gl, width, height, { ...rgba, mipmaps: true }, null);
    this.status = new GridTexture(gl, width, height, { ...rgba, mipmaps: true }, null);
    this.occPrev = new GridTexture(gl, width, height, { ...rgba, mipmaps: true }, null);
    const red = { internal: gl.R8, format: gl.RED, type: gl.UNSIGNED_BYTE, mipmaps: true };
    this.own = new GridTexture(gl, width, height, red, null);
    this.ownPrev = new GridTexture(gl, width, height, red, null);
    this.statusPrev = new GridTexture(gl, width, height, { ...rgba, mipmaps: true }, null);
    this.tint = new GridTexture(gl, width, height, { ...rgba, mipmaps: true }, null);
    this.tintPrev = new GridTexture(gl, width, height, { ...rgba, mipmaps: true }, null);
    if (statics.region) {
      this.region = new GridTexture(gl, width, height, { ...rgba, mipmaps: true }, regionRGBA(statics.region));
      this.regionBytes = statics.region.slice();
    }
  }

  /** The world sent again (a structure built, a breach): only what can
   *  change is updated (track, corridors, regions), not the terrain, which
   *  took most of a second to colour on a big map. */
  refresh(statics: Statics): void {
    const tiles = this.width * this.height;
    const w = this.width;
    // Track and corridors: only what changed, and only its rows uploaded (a
    // structure built changes a few hexes of a map of millions).
    let lo = Infinity;
    let hi = -1;
    const od = this.overlayData;
    for (let i = 0; i < tiles; i++) {
      const track = statics.track[i] ? 255 : 0;
      if (od[i * 4] !== track || od[i * 4 + 1] !== statics.corridor[i]) {
        od[i * 4] = track;
        od[i * 4 + 1] = statics.corridor[i];
        if (i < lo) lo = i;
        hi = i;
      }
    }
    if (hi >= 0) {
      const r0 = Math.floor(lo / w);
      const r1 = Math.floor(hi / w);
      this.overlay.uploadRect(od, 0, r0, w, r1 - r0 + 1);
    }
    if (statics.region) {
      if (!this.region) this.region = new GridTexture(this.gl, this.width, this.height, { internal: this.gl.RGBA8, format: this.gl.RGBA, type: this.gl.UNSIGNED_BYTE, mipmaps: true }, regionRGBA(statics.region));
      else if (!this.regionBytes || !sameBytes(this.regionBytes, statics.region)) this.region.upload(regionRGBA(statics.region));
      this.regionBytes = statics.region.slice();
    }
  }

  /** Every texture freed (a different world replaces this one). */
  dispose(): void {
    for (const t of [this.terrain, this.overlay, this.occ, this.status, this.occPrev, this.own, this.ownPrev, this.statusPrev, this.tint, this.tintPrev, this.vis, this.water, this.region, this.regionPrev, this.territory, this.ore]) t?.delete();
  }

  /** The region layer last uploaded (to see a breach's change). */
  private regionBytes: Uint8Array | null = null;
  /** Where the army textures (occ, status, tint, own and their previous
   *  frames) may hold anything: a renderer after this one uploads only
   *  this and its own hexes on its first frame, not the whole map. */
  armyBox: [number, number, number, number] = [Infinity, Infinity, -Infinity, -Infinity];

  /** A breach at hex `at`: animates from the `before` layer to this one. */
  breach(before: Uint8Array, at: [number, number]): void {
    if (!this.region || before.length !== this.width * this.height) return;
    const gl = this.gl;
    this.regionPrev = new GridTexture(gl, this.width, this.height, { internal: gl.RGBA8, format: gl.RGBA, type: gl.UNSIGNED_BYTE, mipmaps: true }, regionRGBA(before));
    this.breachAt = at;
    this.breachT = 0;
  }

  /** Settled ground: `cols` x `rows` cells of `cell` hexes, RGBA per cell
   * (faction colour times settlement, settlement). */
  setTerritory(cols: number, rows: number, cell: number, data: Uint8Array): void {
    const gl = this.gl;
    if (!this.territory || this.territory.width !== cols || this.territory.height !== rows) {
      this.territory = new GridTexture(gl, cols, rows, { internal: gl.RGBA8, format: gl.RGBA, type: gl.UNSIGNED_BYTE, mipmaps: true }, data);
    } else {
      this.territory.upload(data);
    }
    this.territoryScale = [this.width / (cols * cell), this.height / (rows * cell)];
  }

  /** The ore heatmap: `cols` x `rows` cells of `cell` hexes, RGBA per cell
   * (heat colour times strength, strength). */
  setOre(cols: number, rows: number, cell: number, data: Uint8Array): void {
    const gl = this.gl;
    if (!this.ore || this.ore.width !== cols || this.ore.height !== rows) {
      this.ore = new GridTexture(gl, cols, rows, { internal: gl.RGBA8, format: gl.RGBA, type: gl.UNSIGNED_BYTE, mipmaps: true }, data);
    } else {
      this.ore.upload(data);
    }
    this.oreScale = [this.width / (cols * cell), this.height / (rows * cell)];
  }

  setVisibility(vis: Uint8Array): void {
    this.vis.upload(vis);
  }

  /** Fog changed only within a box (a big map's seen ground is a part). */
  setVisibilityRect(vis: Uint8Array, x: number, y: number, w: number, h: number): void {
    this.vis.uploadRect(vis, x, y, w, h);
  }

  /** Updates the corridor layer (it grows as field depots open). */
  setCorridors(corridor: Uint8Array): void {
    let changed = false;
    for (let i = 0; i < corridor.length; i++) {
      if (this.overlayData[i * 4 + 1] !== corridor[i]) {
        this.overlayData[i * 4 + 1] = corridor[i];
        changed = true;
      }
    }
    if (changed) this.overlay.upload(this.overlayData);
  }

  draw(v: GroundView, factions: [number, number, number][], viewport: [number, number, number, number]): void {
    const gl = this.gl;
    const p = this.program;
    p.use();
    gl.bindVertexArray(this.vao);
    this.terrain.bind(0);
    this.overlay.bind(1);
    this.occ.bind(2);
    this.status.bind(3);
    this.occPrev.bind(4);
    this.own.bind(10);
    this.ownPrev.bind(11);
    gl.uniform1i(p.u("uOwn"), 10);
    gl.uniform1i(p.u("uOwnPrev"), 11);
    this.statusPrev.bind(5);
    this.tint.bind(6);
    this.tintPrev.bind(7);
    gl.uniform1i(p.u("uTint"), 6);
    gl.uniform1i(p.u("uTintPrev"), 7);
    gl.uniform1f(p.u("uTintAmount"), TINT);
    gl.uniform1i(p.u("uOccPrev"), 4);
    gl.uniform1i(p.u("uStatusPrev"), 5);
    gl.uniform1f(p.u("uBlend"), v.blend);
    gl.uniform1i(p.u("uTerrain"), 0);
    gl.uniform1i(p.u("uOverlay"), 1);
    gl.uniform1i(p.u("uOcc"), 2);
    gl.uniform1i(p.u("uStatus"), 3);
    gl.uniform2f(p.u("uCenter"), v.center[0], v.center[1]);
    gl.uniform1f(p.u("uZoom"), v.zoom);
    gl.uniform4f(p.u("uViewport"), ...viewport);
    gl.uniform2i(p.u("uWorld"), this.width, this.height);
    gl.uniform1i(p.u("uZone"), this.zoneSize);
    gl.uniform3fv(p.u("uOwnColor"), factions[v.fog - 1] ?? [0, 0, 0]);
    gl.uniform1f(p.u("uDetail"), v.detail);
    gl.uniform1i(p.u("uMode"), v.mode);
    gl.uniform1i(p.u("uSat"), v.sat);
    gl.uniform1i(p.u("uLayers"), v.layers);
    gl.uniform1i(p.u("uCorridorMask"), v.corridorMask);
    gl.uniform2i(p.u("uHover"), v.hover[0], v.hover[1]);
    this.water.bind(9);
    gl.uniform1i(p.u("uWater"), 9);
    this.vis.bind(8);
    gl.uniform1i(p.u("uVis"), 8);
    gl.uniform1i(p.u("uFog"), v.fog);
    gl.uniform3fv(p.u("uVoid"), v.void ?? [0.039, 0.039, 0.039]);
    gl.uniform1i(p.u("uFancy"), v.fancy === false ? 0 : 1);
    gl.uniform1i(p.u("uStorm"), v.storm ? 1 : 0);
    gl.uniform1f(p.u("uTime"), (v.time ?? 0) % 3600);
    gl.uniform1i(p.u("uHasTerritory"), this.territory && v.territory !== false ? 1 : 0);
    if (this.territory) {
      this.territory.bind(12);
      gl.uniform1i(p.u("uTerritory"), 12);
      gl.uniform2f(p.u("uTerritoryScale"), this.territoryScale[0], this.territoryScale[1]);
    }
    gl.uniform1i(p.u("uHasRegion"), this.region ? 1 : 0);
    if (this.region) {
      this.region.bind(14);
      gl.uniform1i(p.u("uRegion"), 14);
      const playing = this.regionPrev && this.breachT >= 0 && this.breachT < 3.5;
      if (playing) {
        this.regionPrev!.bind(15);
        gl.uniform1i(p.u("uRegionPrev"), 15);
        gl.uniform2f(p.u("uBreach"), this.breachAt[0], this.breachAt[1]);
      } else {
        // Bound to something valid all the same.
        gl.uniform1i(p.u("uRegionPrev"), 14);
      }
      gl.uniform1f(p.u("uBreachT"), playing ? this.breachT : -1);
    }
    gl.uniform1i(p.u("uHasOre"), this.ore && v.ore !== false ? 1 : 0);
    if (this.ore) {
      this.ore.bind(13);
      gl.uniform1i(p.u("uOre"), 13);
      gl.uniform2f(p.u("uOreScale"), this.oreScale[0], this.oreScale[1]);
    }
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }
}
